export type LibraryKind =
  | "skill"
  | "signal"
  | "automation"
  | "idea"
  | "video";

export interface LibraryItem {
  id: string;
  kind: LibraryKind;
  title: string;
  blurb: string;
  meta: string;
  accent: string;
  topic: string;
  cost?: number;
  model?: "Flash" | "Opus";
  sources?: number;
  basePrice?: number;
  uses?: number;
  /** Headline runtime estimate for the card, e.g. "~3 min". */
  runtimeLabel?: string;
  tags?: string[];
  previewMd?: string;
  /** Real per-model prices from the skill's model_estimates (when known). */
  flashPrice?: number;
  opusPrice?: number;
  recommendedModel?: string | null;
  /** GPT-5.4 thumbnail URL (null/undefined until backfilled). */
  thumbnailUrl?: string | null;
  /** WATCH cards — opens in a new tab when set. */
  youtubeUrl?: string;
  /** Optional start offset for YouTube embed / deep link. */
  youtubeStartSeconds?: number;
  /** Mux playback ID — preferred over YouTube for in-app player (no bot wall). */
  muxPlaybackId?: string;
  /** Placeholder card — no link, muted styling. */
  comingSoon?: boolean;
  /** Highlighted "start here" card (e.g. the onboarding tutorial video). */
  featured?: boolean;
  /**
   * SIGNAL-ONLY live track record (from GET /alerts/signals). Drives the mini
   * trading-view chart + stats on the signal card; updates as the signal does.
   */
  signal?: {
    allTimePct: number;
    /** Real capital deployed across all trades ($). */
    deployedUsd: number;
    /** Total profit ($). deployed + profit = current value. */
    profitUsd: number;
    winRate: number; // 0..1
    totalTrades: number;
    openTrades: number;
    /** Downsampled equity-curve values for the card sparkline. */
    curve: number[];
    /** Newest open position — the card thumbnail mirrors the signal page's
        Current Signal(s) view using this. */
    featured?: {
      name: string;
      ticker: string;
      direction: "long" | "short";
      entry: number | null;
      currentPrice: number | null;
      takeProfit: number | null;
      stop: number | null;
      trailPct: number | null;
      timeExitDays: number | null;
      notionalUsd: number | null;
      pnlPct: number | null;
      openedAt: string | null;
    } | null;
  };
}

export const KIND_LABEL: Record<LibraryKind, string> = {
  skill: "Skill",
  signal: "Signal",
  automation: "Automation",
  idea: "User Story",
  video: "Tutorial",
};

/** Section header titles (proper plurals, not just KIND_LABEL + "s"). */
export const SECTION_TITLE: Record<LibraryKind, string> = {
  skill: "Skills",
  signal: "Signals",
  automation: "Automations",
  idea: "User Stories",
  video: "Tutorials",
};

// "alerts" groups Signals + Automations (the subscribable content) into one
// view. See lib/library-routes.ts and LibraryClient's combined branch.
export const TABS: { label: string; value: LibraryKind | "all" | "alerts" }[] = [
  { label: "All", value: "all" },
  { label: "Alerts", value: "alerts" },
  { label: "Skills", value: "skill" },
  { label: "Signals", value: "signal" },
  { label: "Automations", value: "automation" },
  { label: "User Stories", value: "idea" },
  { label: "Tutorials", value: "video" },
];

function make(
  kind: LibraryKind,
  count: number,
  accent: string,
  titles: string[],
): LibraryItem[] {
  return Array.from({ length: count }).map((_, i) => ({
    id: `${kind}-${i + 1}`,
    kind,
    title: titles[i % titles.length],
    blurb: "",
    meta: "",
    accent,
    topic: "",
  }));
}

const SKILL_ACCENT = "#ffffff";

/** [title, flashBasePrice, sources, uses, tags, summary] */
const SKILL_SEED: [string, number, number, number, string[], string][] = [
  ["Ticker Deep Dive: Quick Pulse", 0.3, 18, 4821, ["Options Flow", "Gamma", "Earnings", "Analyst Ratings", "News"], "A fast read on any ticker. Price action, options flow, gamma, earnings and the latest news in one clean pass."],
  ["Ticker Deep Dive: Full", 0.42, 21, 2960, ["Dark Pool", "Dealer Exposure", "Short Interest", "Market Tide", "Volatility"], "A complete single-name workup across flow, dark pool prints, dealer positioning, short interest and fundamentals."],
  ["Ticker Deep Dive: SEC-Grade", 0.55, 27, 1180, ["Fund Agent", "Institutional", "Insider", "Macro Regime", "Income Stmt"], "The deepest dive we offer. Pulls filings, institutional ownership and macro context into one full thesis."],
  ["Find Me a Trade", 0.4, 21, 7340, ["Options Flow", "Dark Pool", "Squeeze", "Net Premiums", "Insider"], "Scans the whole market for the best setup right now, then hands you an entry, a target and a risk level."],
  ["Manage My Portfolio: Deep", 0.6, 26, 1540, ["DCF", "Risk Snapshot", "Drawdown", "ETF Heatmap", "Price Target"], "An advisor-grade review of your holdings. Valuation, risk, drawdown and clear calls on what to trim or add."],
  ["Pre-Market Game Plan", 0.4, 20, 3675, ["Futures", "Market Tide", "Earnings", "FOMC", "Sector Heat"], "Everything moving before the open. Futures, market tide, earnings and the key events that set the day's tone."],
  ["Earnings Play", 0.38, 22, 2210, ["IV Rank", "Term Structure", "Implied Move", "Vol Smile", "Flow"], "Sizes up an earnings event with implied move, IV term structure and how the stock has reacted in the past."],
  ["Deep Screen", 0.55, 20, 980, ["Screener", "OI Changes", "Insider", "Seasonality", "FDA Calendar"], "Builds a custom screen across flow, insider buying, seasonality and catalysts to surface fresh names."],
];

const SKILLS: LibraryItem[] = SKILL_SEED.map(
  ([title, basePrice, sources, uses, tags, summary], i) => ({
    id: `skill-${i + 1}`,
    kind: "skill" as const,
    title,
    blurb: summary,
    meta: "",
    accent: SKILL_ACCENT,
    topic: "",
    basePrice,
    sources,
    uses,
    tags,
  }),
);

export const SKILL_MODELS: {
  key: string;
  label: string;
  icon: string;
  mult: number;
}[] = [
  { key: "gemini", label: "Gemini", icon: "/models/gemini-v2.png", mult: 1 },
  { key: "glm", label: "GLM", icon: "/models/glm.png", mult: 2 },
  { key: "sonnet", label: "Sonnet", icon: "/models/claude-v5.png", mult: 4 },
  { key: "opus", label: "Opus", icon: "/models/claude-v5.png", mult: 6 },
];

const SIGNAL_ACCENT = "#22d3ee";

/** A gently rising equity curve for the mock fallback sparkline. */
function mockCurve(endPct: number, points = 28): number[] {
  const out: number[] = [];
  const start = 10000;
  const end = start * (1 + endPct / 100);
  for (let i = 0; i < points; i++) {
    const t = i / (points - 1);
    const noise = Math.sin(i * 1.7) * (start * 0.012);
    out.push(Math.round(start + (end - start) * t + noise));
  }
  return out;
}

/** [title, allTimePct, deployedUsd, winRate, totalTrades, openTrades] */
const SIGNAL_SEED: [string, number, number, number, number, number][] = [
  ["Dark Pool Conviction Signal", 16.6, 213000, 0.77, 213, 25],
  ["Whale Dip Buyer Signal", 27.2, 42000, 0.74, 42, 13],
  ["Earnings IV Crush Signal", 22.8, 455000, 0.72, 455, 0],
  ["Insider Cluster Buy Signal", 16.7, 46000, 0.91, 46, 14],
];

/** Fallback signal cards (the real 4). Replaced live by GET /alerts/signals. */
const SIGNALS: LibraryItem[] = SIGNAL_SEED.map(
  ([title, allTimePct, deployedUsd, winRate, totalTrades, openTrades], i) => ({
    id: `signal-${i + 1}`,
    kind: "signal" as const,
    title,
    blurb: "",
    meta: "",
    accent: SIGNAL_ACCENT,
    topic: "",
    signal: {
      allTimePct,
      deployedUsd,
      profitUsd: Math.round((deployedUsd * allTimePct) / 100),
      winRate,
      totalTrades,
      openTrades,
      curve: mockCurve(allTimePct),
    },
  }),
);

export const LIBRARY_ITEMS: LibraryItem[] = [
  ...SKILLS,
  ...SIGNALS,
  ...make("automation", 8, "#a78bfa", [
    "Beginner Swing Trades",
    "Sunday Wheel Scanner",
    "Overnight Session Digest",
    "Portfolio Cash Flow",
    "Dividend Intelligence",
    "AI Stock Heatmap",
    "Cheap Options Scanner",
    "Swing Thesis Pulse",
  ]),
  ...make("idea", 6, "#f59e0b", [
    "Caught the NVDA breakout early",
    "Finally understand options flow",
    "My first profitable earnings play",
    "Replaced three tools with Xynth",
    "The dark-pool signal paid for a year",
    "From confused to confident in a week",
  ]),
];
